import { useCallback, useEffect, useRef, useState } from 'react'
import { getLatestNewsActivities } from '@domain/features/news/news-feature'
import type { ActivityExplore } from '@domain/models/activity-explore'
import { getContentRefreshPolicy } from '@presentation/hooks/content-refresh-policy'
import { useAutoRefreshOnForeground } from '@presentation/hooks/useAutoRefreshOnForeground'
import { useSsrPageData } from '@presentation/ssr/useSsrPageData'

export function useLatestActivities(count = 4) {
  const ssrActivities = useSsrPageData()?.home?.latestActivities?.slice(0, count)
  const [activities, setActivities] = useState<ActivityExplore[]>(ssrActivities ?? [])
  const [loading, setLoading] = useState(!ssrActivities)
  const [error, setError] = useState<string | null>(null)
  const requestIdRef = useRef(0)
  const refreshPolicy = getContentRefreshPolicy('home')

  const {
    triggerRefresh,
    lastSuccessAt,
    isRefreshing,
  } = useAutoRefreshOnForeground({
    refresh: async ({ reason }) => {
      const requestId = requestIdRef.current + 1
      requestIdRef.current = requestId
      const showInitialLoading = reason === 'mount' && activities.length === 0

      if (showInitialLoading) {
        setLoading(true)
      }

      try {
        setError(null)
        const fetchPolicy = reason === 'mount' ? 'default' : 'server'
        const result = await getLatestNewsActivities(count, fetchPolicy)
        if (requestId === requestIdRef.current) {
          setActivities(result)
        }
      } catch (loadError) {
        if (requestId === requestIdRef.current) {
          setError(
            loadError instanceof Error
              ? loadError.message
              : 'Failed to load activities',
          )
        }
        throw loadError
      } finally {
        if (showInitialLoading && requestId === requestIdRef.current) {
          setLoading(false)
        }
      }
    },
    staleAfterMs: refreshPolicy.staleAfterMs,
    pollIntervalMs: refreshPolicy.pollIntervalMs,
    minRefreshGapMs: refreshPolicy.minRefreshGapMs,
    maxFailureBackoffMs: refreshPolicy.maxFailureBackoffMs,
  })

  useEffect(() => {
    void triggerRefresh('mount', { force: true })
  }, [triggerRefresh])

  const refresh = useCallback(async () => {
    await triggerRefresh('manual', { force: true })
  }, [triggerRefresh])

  return {
    activities,
    loading,
    error,
    refresh,
    lastUpdatedAt: lastSuccessAt,
    isRefreshing,
  }
}
